import { isEqual } from 'lodash';
import { assign, createMachine } from 'xstate';
import { sendParent } from 'xstate/lib/actions';

import { Recipient } from 'api/api';

import { complementAddress } from '../utils/format';
import { ActorsSharedEvents } from './shared-types';
import { COUNTRIES_WITH_EXTENDED_REGION_VALIDATION } from './suggestion-service';

interface Context {
  address: Recipient | null;
  autocompleteSnapshot: Recipient | null;
  suggestionAddressSnapshot: Recipient | null;
  extendedValidation: boolean;
  country: string;
}

type Events =
  | ActorsSharedEvents
  | { type: 'STREET_READY'; payload: { street: string; street_number: string } }
  | { type: 'SKIP' };

export const createStreetNumberMachine = (
  country: string,
  extendedValidation: boolean,
  initialAddress?: Recipient
) =>
  createMachine(
    {
      predictableActionArguments: true,
      tsTypes: {} as import('./street-number-machine.typegen').Typegen0,
      schema: {
        context: {} as Context,
        events: {} as Events,
      },
      context: {
        address: initialAddress || null,
        autocompleteSnapshot: initialAddress || null,
        suggestionAddressSnapshot: initialAddress || null,
        extendedValidation,
        country,
      },
      id: 'street_number',
      initial: initialAddress ? 'ready' : 'unverified',
      states: {
        unverified: {
          on: {
            AUTOCOMPLETE_CHANGE: {
              cond: 'street_number_available',
              target: 'ready.by_autocomplete',
              actions: ['updateAutocompleteAddressSnapshot', 'updateAddressContext'],
            },
            ADDRESS_CHANGED: {
              actions: 'updateAddressContext',
            },
            SUGGESTION_UPDATE: [
              {
                cond: 'streetAndStreetNumberMatchUserInput',
                target: 'ready.by_suggestion',
                actions: ['updateSuggestionAddressSnapshot', 'sendStreetReady'],
              },
              {
                target: 'ready.by_user_confirmation',
                actions: ['updateSuggestionAddressSnapshot', 'sendSkip'],
              },
            ],
          },
        },
        ready: {
          initial: 'by_autocomplete',
          states: {
            by_user_confirmation: {
              on: {
                ADDRESS_CHANGED: {
                  cond: 'significantFieldChange',
                  target: '#street_number.unverified',
                },
              },
            },
            by_suggestion: {
              on: {
                ADDRESS_CHANGED: {
                  cond: 'addressLinesDifferFromSuggestionSnapshot',
                  target: '#street_number.unverified',
                },
              },
            },
            by_autocomplete: {
              on: {
                ADDRESS_CHANGED: {
                  cond: 'addressLinesDifferFromAutocompleteSnapshot',
                  target: '#street_number.unverified',
                },
              },
            },
          },
          on: {
            AUTOCOMPLETE_CHANGE: [
              { actions: ['updateAddressContext', 'updateAutocompleteAddressSnapshot'] },
              {
                cond: 'street_number_unavailable',
                target: '#street_number.unverified',
                actions: ['updateAddressContext', 'updateAutocompleteAddressSnapshot'],
              },
            ],
          },
        },
      },
    },
    {
      actions: {
        updateSuggestionAddressSnapshot: assign({
          suggestionAddressSnapshot: context => context.address,
        }),
        updateAutocompleteAddressSnapshot: assign({
          autocompleteSnapshot: (_, event) => event.payload.address,
        }),
        sendStreetReady: sendParent((_, event) => ({
          type: 'STREET_READY',
          payload: {
            street: event.payload.suggestion?.address?.street,
            street_number: event.payload.suggestion?.address?.street_number,
          },
        })),
        sendSkip: sendParent({ type: 'SKIP' }),
        updateAddressContext: assign({
          address: (_, event) => event.payload.address,
        }),
      },
      guards: {
        addressLinesDifferFromSuggestionSnapshot: (context, event) => {
          const addressLinesSnapshot = context.suggestionAddressSnapshot?.address_lines;

          const addressLinesChanged = event.payload.address.address_lines;
          if (isEqual(addressLinesSnapshot, addressLinesChanged)) {
            return false;
          }
          return true;
        },
        addressLinesDifferFromAutocompleteSnapshot: (context, event) => {
          const addressLinesSnapshot = context.autocompleteSnapshot?.address_lines;

          const addressLinesChanged = event.payload.address.address_lines;
          if (isEqual(addressLinesSnapshot, addressLinesChanged)) {
            return false;
          }
          return true;
        },
        streetAndStreetNumberMatchUserInput: (context, event) => {
          if (event.payload?.result === 'VALID') {
            return true;
          }

          if (!event.payload?.suggestion?.address) {
            return false;
          }
          const { street_number } = event.payload.suggestion.address;
          if (!street_number) {
            return false;
          }

          const formattedAddressFromSuggestion = complementAddress(
            event.payload.suggestion.address,
            event.payload.suggestion.address.country!
          );

          if (
            formattedAddressFromSuggestion.address_lines?.[0] ===
            context.address?.address_lines?.[0]
          ) {
            return true;
          }
          return false;
        },
        street_number_available: (_, event) => !!event.payload?.address?.street_number,
        street_number_unavailable: (_, event) => !!!event.payload?.address?.street_number,
        significantFieldChange: (ctx, event) =>
          [
            'street',
            'street_number',
            'postal_code',
            'city',
            'address_lines.0',
            ...(ctx.extendedValidation &&
            COUNTRIES_WITH_EXTENDED_REGION_VALIDATION.includes(ctx.country)
              ? ['region']
              : []),
          ].includes(event.payload.field),
      },
    }
  );

export type StreetNumberMachine = ReturnType<typeof createStreetNumberMachine>;
